// Perfil del propio usuario (cualquier rol, ver ruta en App.js): celular de
// WhatsApp y enlace propio del catálogo por vendedor (v1.4). El admin ve
// además los enlaces de todo el equipo y el informe por vendedor — eso vive
// en Gestión de Usuarios / un reporte aparte, no acá.

import React, { useState, useEffect } from 'react';
import { getMiPerfil, updateMiPerfil } from '../services/apiService';
import { FaUserCircle, FaWhatsapp, FaCheckCircle, FaLink, FaCopy, FaExclamationTriangle } from 'react-icons/fa';

const MiPerfilPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [celularWhatsapp, setCelularWhatsapp] = useState('');
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    const fetchPerfil = async () => {
      try {
        setLoading(true);
        const data = await getMiPerfil();
        setPerfil(data);
        setCelularWhatsapp(data.celularWhatsapp || '');
      } catch (err) {
        setError(err.message || 'No se pudo cargar tu perfil.');
      } finally {
        setLoading(false);
      }
    };
    fetchPerfil();
  }, []);

  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => { setMessage(null); setError(null); }, 4000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await updateMiPerfil({ celularWhatsapp: celularWhatsapp.trim() || null });
      setPerfil(res.user);
      setCelularWhatsapp(res.user.celularWhatsapp || '');
      setMessage('✅ Perfil actualizado con éxito.');
    } catch (err) {
      setError(err.message || 'No se pudo actualizar tu perfil.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-emerald-600 animate-pulse font-bold">Cargando tu perfil...</div>;

  // El enlace vive en la propia app del catálogo (mismo dominio del panel),
  // no en un dominio aparte — window.location.origin es correcto tanto en
  // staging como en producción.
  const slug = perfil?.company?.slug;
  const catalogoActivo = perfil?.company?.catalogoPublicoActivo;
  const miEnlace = slug ? `${window.location.origin}/catalogo/${slug}?v=${perfil.codigoVendedor}` : null;

  const copiarEnlace = async () => {
    if (!miEnlace) return;
    try {
      await navigator.clipboard.writeText(miEnlace);
    } catch {
      // Clipboard API puede no estar disponible (ej. sin HTTPS o permiso
      // denegado) — el enlace ya está seleccionable a mano en el input.
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-3xl font-black text-gray-800 tracking-tight">Mi <span className="text-emerald-500">Perfil</span></h2>
        <p className="text-gray-500 text-sm">Tus datos personales dentro de {perfil?.nombreUsuario ? '' : 'la compañía'}.</p>
      </div>

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-6 space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
            <FaUserCircle size={24} />
          </div>
          <div>
            <p className="font-bold text-gray-800">{perfil?.nombreUsuario}</p>
            <p className="text-xs text-gray-400">{perfil?.email}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase ml-1 flex items-center gap-1.5">
              <FaWhatsapp className="text-emerald-500" /> Tu celular de WhatsApp
            </label>
            <input
              type="tel"
              value={celularWhatsapp}
              onChange={(e) => setCelularWhatsapp(e.target.value)}
              placeholder="3001234567"
              className="w-full border border-gray-200 rounded-xl p-2.5 focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
            />
            <p className="text-[11px] text-gray-400 mt-1.5 ml-1">
              Si compartes tu propio enlace del catálogo, los pedidos que traiga te llegarán a este número. Déjalo vacío si prefieres que sigan llegando al WhatsApp de la compañía.
            </p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-gray-900 text-white font-bold py-3 rounded-xl hover:bg-emerald-600 transition-all shadow-lg hover:shadow-emerald-500/20 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </form>

        {message && (
          <p className="text-emerald-600 text-sm font-medium text-center flex items-center justify-center gap-1.5">
            <FaCheckCircle /> {message.replace('✅ ', '')}
          </p>
        )}
        {error && <p className="text-red-500 text-sm font-medium text-center">{error}</p>}
      </div>

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-6 space-y-3">
        <h3 className="font-bold text-gray-700 flex items-center gap-2"><FaLink className="text-emerald-500" /> Tu enlace del catálogo</h3>

        {!slug ? (
          <p className="text-sm text-gray-400 flex items-start gap-2">
            <FaExclamationTriangle className="text-amber-400 mt-0.5 flex-shrink-0" />
            Tu compañía todavía no tiene un catálogo público configurado. Pídele al administrador que lo configure en "Configuración del catálogo".
          </p>
        ) : !catalogoActivo ? (
          <p className="text-sm text-gray-400 flex items-start gap-2">
            <FaExclamationTriangle className="text-amber-400 mt-0.5 flex-shrink-0" />
            El catálogo está configurado pero desactivado. Pídele al administrador que lo active en "Configuración del catálogo".
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              Comparte este enlace: los pedidos que traiga quedan a tu nombre y, si configuraste tu celular arriba, te llegan directo a ti por WhatsApp.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={miEnlace}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-600 outline-none"
              />
              <button
                type="button"
                onClick={copiarEnlace}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all active:scale-95"
              >
                {copiado ? <FaCheckCircle /> : <FaCopy />} {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
            {!perfil?.celularWhatsapp && (
              <p className="text-[11px] text-amber-500 mt-1">
                Ojo: como todavía no configuraste tu celular arriba, por ahora los pedidos de este enlace le llegan al WhatsApp de la compañía, no a ti.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MiPerfilPage;
